import {
  Alert,
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormControl,
  FormField,
  FormSubmitHandler,
  openConfirmDialog,
} from 'platform/components';
import {Heading, Image, Show, Text, VStack, HStack, Box, Space} from 'platform/foundation';
import styled from 'styled-components';
import {z} from 'zod';

import {useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {useLazyCalculateFromWithVatQuery} from '@dms/api/core';
import {useGetSalePresetSettingsListQuery, useGetWatermarkListQuery} from '@dms/api/sales';
import {MagicPublishingMissingField, useCreateMagicPublishingMutation} from '@dms/api/settings';
import {VatRateType} from '@dms/api/shared';
import {
  FuelTypeEnum,
  UpdateVehicleApiArg,
  useGetSaleVehicleQuery,
  useGetVehicleQuery,
  usePatchSaleVehicleSalePresetMutation,
  usePatchSaleVehicleWatermarkMutation,
  usePatchVehiclePricesMutation,
  useUpdateVehicleMutation,
  VehicleTypeEnum,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  handleApiError,
  mandatoryFieldsMap,
  useTenant,
  useVehicleCatalogue,
  useVehicleMake,
  useVehicleModel,
} from '@dms/shared';
import {VehiclePhotosRoutes} from '@dms/teas';

import {
  ApiError,
  buildObject,
  composePath,
  getSafely,
  isEmptyZodValue,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {formatOverLimitWarnings} from '../utils/formatOverLimitWarnings';
import {OverLimitPlatform} from '../utils/getOverLimitPlatforms';

interface MagicPublishingFormProps extends RequiredTestIdProps {
  vehicleId: string;
  missingFields: MagicPublishingMissingField[];
  missingMandatoryFields: string[];
  hasSalesPhotos: boolean;
  overLimitPlatforms: OverLimitPlatform[];
  onSuccess?: () => void;
  onRequestCreated?: (requestId: string) => void;
}

const MagicPublishingFormSchema = z.object({
  SELLING_PRICE: z.number().optional(),
  isVatDeductible: z.boolean().optional(),
  MILEAGE: z.number().optional(),
  MAKE: z.string().optional(),
  MODEL: z.string().optional(),
  POWER: z.number().optional(),
  BODY: z.array(z.string()).optional(),
  REAL_PRODUCTION_YEAR: z.number().optional(),
  TRANSMISSION: z.array(z.string()).optional(),
  FUEL: z.array(z.string()).optional(),
  DRIVE: z.array(z.string()).optional(),
  salePresetId: z.string().nullable().optional(),
  watermarkId: z.string().nullable().optional(),
});

type MagicPublishingFormValues = z.infer<typeof MagicPublishingFormSchema>;

// Uses colors for one-time purpose
// eslint-disable-next-line eag/no-css-property
const StyledImageWrapper = styled.div`
  background: radial-gradient(100% 211.57% at 0% 0%, #a9c9fe 0%, #dfd5ff 49.5%, #f4f3f9 100%);
  border-radius: ${({theme}) => theme.radii.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${({theme}) => theme.getSize(50)};
`;

export function MagicPublishingForm(props: MagicPublishingFormProps) {
  const {tenantCurrency, tenantCountry} = useTenant();

  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId: props.vehicleId});
  const {data: vehicleData} = useGetVehicleQuery({vehicleId: props.vehicleId});

  const vehicleType = vehicleData?.type ?? null;

  const {data: salePresets} = useGetSalePresetSettingsListQuery();
  const {data: watermarks} = useGetWatermarkListQuery();

  const [{groupedMakeOptionsWithTopMakes}, {isLoading: isMakeLoading}] =
    useVehicleMake(vehicleType);

  const [vehicleCatalogue] = useVehicleCatalogue(vehicleType);

  const [updateVehicle, {isLoading: isUpdatingVehicle}] = useUpdateVehicleMutation();
  const [patchVehiclePrices, {isLoading: isPatchingPrices}] = usePatchVehiclePricesMutation();
  const [patchSalePreset, {isLoading: isPatchingSalePreset}] =
    usePatchSaleVehicleSalePresetMutation();
  const [patchWatermark, {isLoading: isPatchingWatermark}] = usePatchSaleVehicleWatermarkMutation();
  const [createMagicPublishing, {isLoading: isCreatingMagicPublishing}] =
    useCreateMagicPublishingMutation();
  const [calculateFromWithVat] = useLazyCalculateFromWithVatQuery();

  const isSubmitting =
    isUpdatingVehicle ||
    isPatchingPrices ||
    isPatchingSalePreset ||
    isPatchingWatermark ||
    isCreatingMagicPublishing;

  const isFieldMissing = (field: MagicPublishingMissingField) =>
    props.missingFields.includes(field);

  const hasMissingFields = props.missingFields.length > 0;

  const hasMissingMandatoryFields = props.missingMandatoryFields.length > 0;

  const translatedMandatoryFields = props.missingMandatoryFields.map((field) => {
    const mapping = getSafely(mandatoryFieldsMap, field);
    return mapping?.title ?? field;
  });

  const editVehicleUrl = composePath(vehiclesRoutes.editV2, {
    params: {id: props.vehicleId},
  });

  const schema = MagicPublishingFormSchema.superRefine((data, ctx) => {
    const requiredMessage = i18n.t('general.validations.fieldIsRequired');
    const shape = MagicPublishingFormSchema.shape;

    props.missingFields.forEach((field) => {
      const zodType = getSafely(shape, field) ?? z.undefined();
      const value = getSafely(data, field) ?? z.undefined();

      if (isEmptyZodValue(value, zodType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: requiredMessage,
          path: [field],
        });
      }
    });
  });

  const vehicleFieldsMissing = props.missingFields.some((field) =>
    [
      'MAKE',
      'MODEL',
      'MILEAGE',
      'POWER',
      'BODY',
      'REAL_PRODUCTION_YEAR',
      'TRANSMISSION',
      'FUEL',
      'DRIVE',
    ].includes(field)
  );

  const executeMagicPublishing = async (values: MagicPublishingFormValues) => {
    // Step 1: Update vehicle basic data if any vehicle fields are missing
    if (vehicleFieldsMissing) {
      const vehiclePayload = buildObject<UpdateVehicleApiArg['updateVehicleRequestBody']>()
        .make(values.MAKE ?? vehicleData?.make ?? '')
        .modelFamily(values.MODEL ?? vehicleData?.modelFamily ?? '')
        .type(vehicleData?.type as VehicleTypeEnum)
        .mileage(values.MILEAGE, isFieldMissing('MILEAGE') && isNotNil(values.MILEAGE))
        .power(values.POWER, isFieldMissing('POWER') && isNotNil(values.POWER))
        .bodyStyle(values.BODY?.[0], isFieldMissing('BODY') && Boolean(values.BODY?.[0]))
        .manufacturedOn(
          values.REAL_PRODUCTION_YEAR?.toString(),
          isFieldMissing('REAL_PRODUCTION_YEAR') && isNotNil(values.REAL_PRODUCTION_YEAR)
        )
        .transmission(
          values.TRANSMISSION?.[0],
          isFieldMissing('TRANSMISSION') && Boolean(values.TRANSMISSION?.[0])
        )
        .fuelType(
          values.FUEL?.[0] as FuelTypeEnum,
          isFieldMissing('FUEL') && Boolean(values.FUEL?.[0])
        )
        .drive(values.DRIVE?.[0], isFieldMissing('DRIVE') && Boolean(values.DRIVE?.[0]))
        .build();

      try {
        await updateVehicle({
          vehicleId: props.vehicleId,
          updateVehicleRequestBody: {
            ...vehicleData,
            ...vehiclePayload,
          },
        }).unwrap();
      } catch (error) {
        handleApiError(error as ApiError);
        return;
      }
    }

    // Step 2: Update selling price if missing
    if (isFieldMissing('SELLING_PRICE') && isNotNil(values.SELLING_PRICE)) {
      // S = Standard VAT rate, Z = Zero VAT rate
      const vatCode: VatRateType = values.isVatDeductible ? 'S' : 'Z';
      const priceWithVat = String(values.SELLING_PRICE);

      try {
        const vatCalculation = await calculateFromWithVat({
          countryCode: tenantCountry,
          vatRateType: vatCode,
          amount: priceWithVat,
        }).unwrap();

        await patchVehiclePrices({
          vehicleId: props.vehicleId,
          patchVehiclePricesRequestBody: {
            isVatDeductible: values.isVatDeductible,
            sellingPrice: {
              vatCode,
              withVat: priceWithVat,
              withoutVat: vatCalculation.withoutVat,
              vatRate: vatCalculation.vatRate,
            },
          },
        }).unwrap();
      } catch (error) {
        handleApiError(error as ApiError);
        return;
      }
    }

    // Step 3: Update sale preset and watermark (in parallel)
    const salePresetChanged = values.salePresetId !== saleVehicle?.salePresetId;
    const watermarkChanged = values.watermarkId !== saleVehicle?.watermarkId;

    const patchPromises: Promise<unknown>[] = [];

    if (salePresetChanged) {
      patchPromises.push(
        patchSalePreset({
          vehicleId: props.vehicleId,
          patchSaleVehicleSalePresetRequestBody: {
            salePresetId: values.salePresetId ?? null,
          },
        }).unwrap()
      );
    }

    if (watermarkChanged) {
      patchPromises.push(
        patchWatermark({
          vehicleId: props.vehicleId,
          patchSaleVehicleWatermarkRequestBody: {
            watermarkId: values.watermarkId ?? null,
          },
        }).unwrap()
      );
    }

    if (patchPromises.length > 0) {
      try {
        await Promise.all(patchPromises);
      } catch (error) {
        handleApiError(error as ApiError);
        return;
      }
    }

    // Step 4: Create magic publishing request
    await createMagicPublishing({vehicleId: props.vehicleId})
      .unwrap()
      .then((response) => {
        props.onRequestCreated?.(response.id);
        closeCurrentDialog();
        props.onSuccess?.();
      })
      .catch(handleApiError);
  };

  const handleSubmit: FormSubmitHandler<MagicPublishingFormValues> = async (values) => {
    if (props.overLimitPlatforms.length > 0) {
      openConfirmDialog({
        text: `${i18n.t('page.advertisement.labels.characterLimitExceeded')}\n\n${formatOverLimitWarnings(props.overLimitPlatforms)}`,
        onConfirm: () => executeMagicPublishing(values),
      });
      return;
    }

    await executeMagicPublishing(values);
  };

  const photosUrl = composePath(vehiclesRoutes.photos, {
    params: {id: props.vehicleId, tab: VehiclePhotosRoutes.vehiclePhotos},
  });

  const salePresetOptions =
    salePresets?.map((preset) => ({
      label: preset.name,
      value: preset.id,
    })) ?? [];

  const watermarkOptions =
    watermarks?.map((watermark) => ({
      label: watermark.name,
      value: watermark.id,
    })) ?? [];

  const missingFieldsDefaults = props.missingFields.reduce(
    (acc, field) => ({...acc, [field]: null}),
    {} as Partial<MagicPublishingFormValues>
  );

  const defaultValues: MagicPublishingFormValues = {
    ...missingFieldsDefaults,
    salePresetId: saleVehicle?.salePresetId ?? null,
    watermarkId: saleVehicle?.watermarkId ?? null,
    isVatDeductible: true,
  };

  return (
    <Form<MagicPublishingFormValues>
      onSubmit={handleSubmit}
      experimentalZodSchema={schema}
      defaultValues={defaultValues}
    >
      {(control, formApi) => (
        <>
          <HStack spacing={6}>
            <StyledImageWrapper>
              <Image
                src="./assets/images/ai_logo.png"
                alt={i18n.t('entity.vehicle.labels.magicPublishingTitle')}
                width={24}
                height={24}
                fit="contain"
              />
            </StyledImageWrapper>
            <Box paddingVertical={4}>
              <VStack spacing={4} grow={1}>
                <Heading size={3}>{i18n.t('entity.vehicle.labels.magicPublishingTitle')}</Heading>

                <Text size="small" color="secondary">
                  {i18n.t('entity.vehicle.labels.magicPublishingDescription')}
                </Text>

                <Show
                  when={
                    !hasMissingMandatoryFields &&
                    !hasMissingFields &&
                    props.hasSalesPhotos &&
                    formApi.formState.isValid
                  }
                >
                  <Alert
                    type="inline"
                    variant="success"
                    title={i18n.t('entity.vehicle.notifications.magicPublishingReady')}
                    data-testid={suffixTestId('readyForPublishingAlert', props)}
                  />
                </Show>

                <Show when={!props.hasSalesPhotos}>
                  <Alert
                    type="inline"
                    variant="error"
                    title={i18n.t('entity.vehicle.labels.noPhotosWarning')}
                    hyperlinks={[
                      {
                        title: i18n.t('entity.vehicle.actions.goToPhotos'),
                        href: photosUrl,
                        onClick: closeCurrentDialog,
                      },
                    ]}
                    data-testid={suffixTestId('noPhotosAlert', props)}
                  />
                </Show>

                <Show when={hasMissingMandatoryFields}>
                  <Alert
                    type="inline"
                    variant="error"
                    title={i18n.t('entity.vehicle.labels.missingMandatoryFieldsTitle')}
                    message={translatedMandatoryFields.join(', ')}
                    hyperlinks={[
                      {
                        title: i18n.t('entity.vehicle.actions.goToEditVehicle'),
                        href: editVehicleUrl,
                        onClick: closeCurrentDialog,
                      },
                    ]}
                    data-testid={suffixTestId('missingMandatoryFieldsAlert', props)}
                  />
                </Show>

                <Show when={hasMissingFields}>
                  <Text>{i18n.t('entity.vehicle.labels.addMissingInfo')}</Text>
                </Show>

                <VStack maxWidth={80} spacing={4}>
                  <Show when={isFieldMissing('SELLING_PRICE')}>
                    <FormField
                      control={control}
                      type="number"
                      name="SELLING_PRICE"
                      label={i18n.t('entity.vehicle.labels.salePriceWithVat')}
                      suffix={tenantCurrency}
                      isRequired
                      data-testid={suffixTestId('sellingPrice', props)}
                    />
                    <FormField
                      control={control}
                      type="switch"
                      name="isVatDeductible"
                      label={i18n.t('general.labels.vatDeductible')}
                      data-testid={suffixTestId('isVatDeductible', props)}
                    />
                  </Show>

                  <Show when={isFieldMissing('MILEAGE')}>
                    <FormField
                      control={control}
                      type="number"
                      name="MILEAGE"
                      label={i18n.t('entity.vehicle.labels.mileage')}
                      suffix="km"
                      isRequired
                      data-testid={suffixTestId('mileage', props)}
                    />
                  </Show>

                  <FormField
                    control={control}
                    type="choice"
                    name="salePresetId"
                    label={i18n.t('entity.vehicle.labels.salesProgram')}
                    options={salePresetOptions}
                    data-testid={suffixTestId('salePresetId', props)}
                  />

                  <FormField
                    control={control}
                    type="choice"
                    name="watermarkId"
                    label={i18n.t('entity.vehicle.labels.watermark')}
                    options={watermarkOptions}
                    data-testid={suffixTestId('watermarkId', props)}
                  />

                  <Show when={isFieldMissing('MAKE')}>
                    <FormField
                      control={control}
                      type="choice"
                      name="MAKE"
                      label={i18n.t('entity.vehicle.labels.make')}
                      options={groupedMakeOptionsWithTopMakes ?? []}
                      isLoading={isMakeLoading}
                      isRequired
                      data-testid={suffixTestId('make', props)}
                    />
                  </Show>

                  <Show when={isFieldMissing('MODEL')}>
                    <ModelField
                      control={control}
                      vehicleType={vehicleType}
                      data-testid={suffixTestId('model', props)}
                    />
                  </Show>

                  <Show when={isFieldMissing('POWER')}>
                    <FormField
                      control={control}
                      type="number"
                      name="POWER"
                      label={i18n.t('entity.vehicle.labels.power')}
                      suffix="kW"
                      isRequired
                      data-testid={suffixTestId('power', props)}
                    />
                  </Show>

                  <Show when={isFieldMissing('BODY')}>
                    <FormField
                      control={control}
                      type="chips"
                      name="BODY"
                      label={i18n.t('entity.vehicle.labels.body')}
                      options={vehicleCatalogue?.vehicleStyleOptions ?? []}
                      isRequired
                      data-testid={suffixTestId('bodyStyle', props)}
                    />
                  </Show>

                  <Show when={isFieldMissing('REAL_PRODUCTION_YEAR')}>
                    <FormField
                      control={control}
                      type="number"
                      name="REAL_PRODUCTION_YEAR"
                      label={i18n.t('entity.vehicle.labels.realProductionYear')}
                      isRequired
                      data-testid={suffixTestId('realProductionYear', props)}
                    />
                  </Show>

                  <Show when={isFieldMissing('TRANSMISSION')}>
                    <FormField
                      control={control}
                      type="chips"
                      name="TRANSMISSION"
                      label={i18n.t('entity.vehicle.labels.transmission')}
                      options={vehicleCatalogue?.transmissionOptions ?? []}
                      isRequired
                      data-testid={suffixTestId('transmission', props)}
                    />
                  </Show>

                  <Show when={isFieldMissing('FUEL')}>
                    <FormField
                      control={control}
                      type="chips"
                      name="FUEL"
                      label={i18n.t('entity.vehicle.labels.fuelType')}
                      options={vehicleCatalogue?.fuelTypeOptions ?? []}
                      isRequired
                      data-testid={suffixTestId('fuelType', props)}
                    />
                  </Show>

                  <Show when={isFieldMissing('DRIVE')}>
                    <FormField
                      control={control}
                      type="chips"
                      name="DRIVE"
                      label={i18n.t('entity.vehicle.labels.drive')}
                      options={vehicleCatalogue?.driveOptions ?? []}
                      isRequired
                      data-testid={suffixTestId('drive', props)}
                    />
                  </Show>
                </VStack>
                <Space vertical={4} />

                <ButtonGroup align="right">
                  <Button
                    variant="secondary"
                    onClick={closeCurrentDialog}
                    title={i18n.t('general.actions.cancel')}
                    data-testid={suffixTestId('cancel', props)}
                  />
                  <FormButton
                    variant="primary"
                    type="submit"
                    control={control}
                    title={i18n.t('general.actions.confirm')}
                    isLoading={isSubmitting}
                    isDisabled={
                      hasMissingMandatoryFields ||
                      !props.hasSalesPhotos ||
                      !formApi.formState.isValid
                    }
                    data-testid={suffixTestId('confirm', props)}
                  />
                </ButtonGroup>
              </VStack>
            </Box>
          </HStack>
        </>
      )}
    </Form>
  );
}

interface ModelFieldProps extends RequiredTestIdProps {
  control: FormControl<MagicPublishingFormValues>;
  vehicleType: string | Nullish;
}

function ModelField(props: ModelFieldProps) {
  const selectedMake = useWatch({control: props.control, name: 'MAKE'});

  const makes = selectedMake ? [selectedMake] : null;

  const [{groupedModelOptionsByMake}, {isLoading: isModelLoading}] = useVehicleModel(
    props.vehicleType,
    makes
  );

  return (
    <FormField
      control={props.control}
      type="choice"
      name="MODEL"
      label={i18n.t('entity.vehicle.labels.modelFamily')}
      options={groupedModelOptionsByMake ?? []}
      isLoading={isModelLoading}
      isRequired
      isDisabled={!selectedMake}
      data-testid={suffixTestId('model', props)}
    />
  );
}
